/**
 * Utility functions for formatting and handling phone numbers
 */

interface CountryCodeInfo {
  dialCode: string;
  label: string;
}

const countryCodes: Record<string, CountryCodeInfo> = {
  '+1': { dialCode: '+1', label: 'US/CA' },
  '+44': { dialCode: '+44', label: 'UK' },
  '+61': { dialCode: '+61', label: 'AU' },
  '+49': { dialCode: '+49', label: 'DE' },
  '+33': { dialCode: '+33', label: 'FR' },
  '+81': { dialCode: '+81', label: 'JP' },
  '+82': { dialCode: '+82', label: 'KR' },
  '+86': { dialCode: '+86', label: 'CN' },
  '+91': { dialCode: '+91', label: 'IN' },
  '+52': { dialCode: '+52', label: 'MX' },
  '+55': { dialCode: '+55', label: 'BR' },
  '+34': { dialCode: '+34', label: 'ES' },
  '+39': { dialCode: '+39', label: 'IT' },
  '+31': { dialCode: '+31', label: 'NL' },
  '+46': { dialCode: '+46', label: 'SE' },
  '+47': { dialCode: '+47', label: 'NO' },
  '+45': { dialCode: '+45', label: 'DK' },
  '+358': { dialCode: '+358', label: 'FI' },
  '+41': { dialCode: '+41', label: 'CH' },
};

/**
 * Format phone number for display in tables and UI
 * Converts "+11234567890" to "+1 (123) 456-7890" for US/CA numbers
 * or "+44 1234 567890" for international numbers
 */
export const formatPhoneNumberForDisplay = (phoneNumber?: string | null): string => {
  if (!phoneNumber || !phoneNumber.startsWith('+')) {
    return '—';
  }

  // Find the country code
  const countryCode = Object.keys(countryCodes)
    .sort((a, b) => b.length - a.length) // Sort by length descending to match longer codes first
    .find((code) => phoneNumber.startsWith(code));

  if (!countryCode) {
    return phoneNumber; // Return as-is if no country code found
  }

  const localNumber = phoneNumber.slice(countryCode.length);
  const numbersOnly = localNumber.replace(/\D/g, '');

  if (countryCode === '+1') {
    // US/Canada formatting: +1 (123) 456-7890
    if (numbersOnly.length === 10) {
      return `${countryCode} (${numbersOnly.slice(0, 3)}) ${numbersOnly.slice(
        3,
        6,
      )}-${numbersOnly.slice(6)}`;
    }
  } else {
    // International formatting: +XX XXXX XXXXXX
    if (numbersOnly.length >= 6) {
      if (numbersOnly.length <= 8) {
        return `${countryCode} ${numbersOnly.slice(0, 4)} ${numbersOnly.slice(4)}`;
      } else {
        return `${countryCode} ${numbersOnly.slice(0, 4)} ${numbersOnly.slice(
          4,
          8,
        )} ${numbersOnly.slice(8)}`;
      }
    }
  }

  // Fallback: just add space after country code
  return `${countryCode} ${numbersOnly}`;
};

/**
 * Parse phone number into country code and local number parts
 */
export const parsePhoneNumber = (
  phoneNumber?: string | null,
): { countryCode: string; localNumber: string } | null => {
  if (!phoneNumber || !phoneNumber.startsWith('+')) {
    return null;
  }

  const countryCode = Object.keys(countryCodes)
    .sort((a, b) => b.length - a.length) // Sort by length descending to match longer codes first
    .find((code) => phoneNumber.startsWith(code));

  if (!countryCode) {
    return null;
  }

  const localNumber = phoneNumber.slice(countryCode.length);
  return { countryCode, localNumber };
};

/**
 * Validate that a phone number has a valid format
 */
export const isValidPhoneNumber = (phoneNumber?: string | null): boolean => {
  if (!phoneNumber) return true; // Optional field

  const parsed = parsePhoneNumber(phoneNumber);
  if (!parsed) return false;

  const numbersOnly = parsed.localNumber.replace(/\D/g, '');

  // US/Canada numbers should be 10 digits
  if (parsed.countryCode === '+1') {
    return numbersOnly.length === 10;
  }

  // International numbers should be 6-15 digits (ITU-T recommendation)
  return numbersOnly.length >= 6 && numbersOnly.length <= 15;
};
